import { useEffect, useState } from "react";
import { fz, toastAward } from "@/lib/fanzone";
import { toast } from "sonner";
import { CalendarCheck, Home, Plane, Trophy, CheckCircle2, XCircle, Crown } from "lucide-react";
import ShareCardPanel from "@/components/fanzone/ShareCardPanel";

export default function SeasonPredictor() {
  const [data, setData] = useState(null);
  const [lb, setLb] = useState(null);
  const [picks, setPicks] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = () => {
    fz.predict.games().then(({ data: d }) => {
      setData(d);
      if (d.my_picks) { setPicks(d.my_picks); setSaved(true); }
    }).catch(() => {});
    fz.predict.leaderboard().then(({ data: d }) => setLb(d)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="titans-card p-6 text-titans-silver text-sm">Loading the schedule…</div>;

  const upcoming = (data.games || []).filter((g) => !g.completed);
  const finished = (data.games || []).filter((g) => g.completed && g.result);

  if (!upcoming.length && !finished.length) {
    return <div className="titans-card p-6 text-titans-silver text-sm" data-testid="fz-predict-empty">The season schedule isn't posted yet — check back after the schedule release.</div>;
  }

  const wins = upcoming.filter((g) => picks[g.id] === "W").length;
  const losses = upcoming.filter((g) => picks[g.id] === "L").length;
  const allPicked = upcoming.every((g) => picks[g.id]);

  const pick = (id, v) => setPicks((p) => ({ ...p, [id]: v }));

  const submit = async () => {
    if (!allPicked || busy) return;
    setBusy(true);
    try {
      const { data: r } = await fz.predict.submit(picks);
      toast.success(`Prediction locked: ${r.record} season`);
      if (r.award) toastAward(r.award);
      setSaved(true);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save picks");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="fz-predict">
      <div className="titans-card p-5 md:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-2">
            <CalendarCheck size={16} className="text-titans-blue" />
            <span className="font-display text-xl uppercase">Predict Every Game</span>
          </div>
          <div className="flex items-center gap-4">
            {data.my_points > 0 && (
              <div className="text-right">
                <div className="font-display text-3xl leading-none tabular-nums text-titans-blue flex items-center gap-1 justify-end">
                  <Trophy size={16} className="text-amber-500" />
                  <span data-testid="fz-predict-points">{data.my_points}</span>
                </div>
                <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Points banked</div>
              </div>
            )}
            <div className="text-right">
              <div className="font-display text-3xl leading-none tabular-nums" data-testid="fz-predict-record">
                <span className="text-emerald-600">{wins}</span>
                <span className="text-titans-silver">–</span>
                <span className="text-titans-red">{losses}</span>
              </div>
              <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">Your projection</div>
            </div>
            <button
              onClick={submit}
              disabled={!allPicked || busy}
              data-testid="fz-predict-submit"
              className="bg-titans-red hover:bg-titans-red-hover text-white px-6 py-3 font-cond uppercase tracking-wider text-xs disabled:opacity-40"
            >
              {saved ? "Update Picks" : "Lock Season"}
            </button>
          </div>
        </div>

        {finished.length > 0 && (
          <div className="mb-5" data-testid="fz-predict-graded">
            <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue mb-2">
              Graded — {data.points_per_win || 10} pts per correct call
            </div>
            <div className="space-y-1.5">
              {finished.map((g, i) => {
                const grade = data.my_graded?.[g.id];
                return (
                  <div key={g.id} className="flex items-center gap-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2" data-testid={`fz-predict-final-${i}`}>
                    <span className="font-cond uppercase tracking-wider text-[10px] text-titans-silver w-10 shrink-0">Wk {g.week || "—"}</span>
                    {g.opponent.logo && <img src={g.opponent.logo} alt={g.opponent.abbr} className="w-6 h-6 object-contain shrink-0" />}
                    <span className="font-cond uppercase tracking-wide text-sm flex-1 truncate">
                      {g.home ? "vs" : "@"} {g.opponent.name}
                    </span>
                    <span className={`font-display text-sm px-2 py-0.5 rounded ${g.result === "W" ? "bg-emerald-600 text-white" : "bg-titans-red text-white"}`}>
                      {g.result === "W" ? "TITANS WIN" : "TITANS LOSS"}
                    </span>
                    {grade ? (
                      grade.correct ? (
                        <span className="inline-flex items-center gap-1 font-cond uppercase tracking-wider text-[10px] text-emerald-600 shrink-0">
                          <CheckCircle2 size={12} /> +{data.points_per_win || 10}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 font-cond uppercase tracking-wider text-[10px] text-titans-red shrink-0">
                          <XCircle size={12} /> Picked {grade.pick}
                        </span>
                      )
                    ) : (
                      <span className="font-cond uppercase tracking-wider text-[10px] text-titans-muted shrink-0">No pick</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          {upcoming.map((g, i) => {
            const cons = saved && data.consensus?.[g.id] !== undefined ? data.consensus[g.id] : null;
            return (
              <div key={g.id} className={`rounded-lg border px-3 py-2.5 ${picks[g.id] ? "border-[#E2E8F0]" : "border-titans-blue/40 bg-titans-blue/5"}`} data-testid={`fz-predict-game-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="font-cond uppercase tracking-wider text-[10px] text-titans-silver w-10 shrink-0">Wk {g.week || i + 1}</span>
                  {g.opponent.logo && <img src={g.opponent.logo} alt={g.opponent.abbr} className="w-7 h-7 object-contain shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-cond uppercase tracking-wide text-sm truncate flex items-center gap-1.5">
                      {g.home ? <Home size={11} className="text-titans-blue shrink-0" /> : <Plane size={11} className="text-titans-silver shrink-0" />}
                      {g.home ? "vs" : "@"} {g.opponent.name}
                    </div>
                    <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">
                      {g.date ? new Date(g.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "TBD"}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => pick(g.id, "W")}
                      data-testid={`fz-predict-w-${i}`}
                      className={`w-11 py-2 font-display text-base rounded-md border transition-colors ${
                        picks[g.id] === "W" ? "bg-emerald-600 border-emerald-600 text-white" : "border-[#E2E8F0] text-titans-silver hover:border-emerald-500"
                      }`}
                    >
                      W
                    </button>
                    <button
                      onClick={() => pick(g.id, "L")}
                      data-testid={`fz-predict-l-${i}`}
                      className={`w-11 py-2 font-display text-base rounded-md border transition-colors ${
                        picks[g.id] === "L" ? "bg-titans-red border-titans-red text-white" : "border-[#E2E8F0] text-titans-silver hover:border-titans-red"
                      }`}
                    >
                      L
                    </button>
                  </div>
                </div>
                {cons !== null && (
                  <div className="flex items-center gap-2 mt-1.5 pl-[52px]">
                    <div className="flex-1 h-1.5 rounded-full bg-titans-red/25 overflow-hidden max-w-[220px]">
                      <div className="h-full bg-emerald-500" style={{ width: `${cons}%` }} />
                    </div>
                    <span className="font-cond uppercase tracking-wider text-[9px] text-titans-silver">{cons}% of fans say win</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 font-cond uppercase tracking-wider text-[10px] text-titans-muted text-center">
          {data.total_fans > 0 ? `${data.total_fans} fan${data.total_fans === 1 ? "" : "s"} have locked a season` : "Be the first fan to lock a full season"} · 75 XP for your first submission · {lb?.points_per_win || 10} pts per correct pick once games go final
        </div>
      </div>

      {lb && lb.entries?.length > 0 && (
        <div className="titans-card p-5 md:p-6" data-testid="fz-prophet-race">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Crown size={15} className="text-titans-blue" />
              <span className="font-display text-xl uppercase">The Prophet Race</span>
            </div>
            <span className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">
              {lb.games_final > 0 ? `${lb.games_final} game${lb.games_final === 1 ? "" : "s"} graded` : "Grading starts when games go final"}
            </span>
          </div>
          <div className="space-y-1">
            {lb.entries.slice(0, 10).map((e, i) => (
              <div key={i} className="flex items-center gap-3 px-2 py-1.5 rounded-lg" data-testid={`fz-prophet-row-${i}`}>
                <span className={`font-display text-base w-6 text-center ${e.rank === 1 ? "text-titans-red" : e.rank <= 3 ? "text-titans-blue" : "text-titans-silver"}`}>{e.rank}</span>
                <span className="font-cond uppercase tracking-wide text-sm flex-1 truncate">{e.name}</span>
                <span className="font-cond uppercase tracking-wider text-[10px] text-titans-silver shrink-0">{e.correct}/{e.graded} right</span>
                <span className="font-display text-lg text-titans-blue tabular-nums shrink-0">{e.points}</span>
              </div>
            ))}
          </div>
          {lb.me && !lb.entries.some((e) => e.name === lb.me.name) && (
            <div className="mt-2 flex items-center gap-3 px-2 py-1.5 rounded-lg bg-titans-blue/10 border border-titans-blue/40" data-testid="fz-prophet-me">
              <span className="font-cond uppercase tracking-wide text-sm flex-1">{lb.me.name || "You (unnamed — claim a name to enter the race)"}</span>
              <span className="font-display text-lg text-titans-blue tabular-nums">{lb.me.points}</span>
            </div>
          )}
        </div>
      )}

      {saved && (
        <div className="font-cond uppercase tracking-wider text-[10px] text-titans-muted text-center">
          Picks lock automatically as each game kicks off — correct calls bank points and XP all season long
        </div>
      )}
    </div>
  );
}
